import { captureException } from '@sentry/react-router'
import * as React from 'react'
import {
	type ErrorResponse,
	isRouteErrorResponse,
	Link,
	useParams,
	useRouteError,
} from 'react-router'
import { reportErrorToServer } from '#app/utils/client-error-report.ts'
import { getErrorMessage } from '#app/utils/misc.tsx'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => React.JSX.Element | null

/** what the house looks like: nobody home, or the light acting up */
type Mood = 'away' | 'flicker'

const STATUS_COPY: Record<number, { title: string; text: string }> = {
	400: {
		title: 'Det der skjøna vi ikkje',
		text: 'Noko i førespurnaden var feil. Gå tilbake og prøv igjen.',
	},
	401: {
		title: 'Du må logge inn først',
		text: 'Logg inn, så kjem du rett tilbake hit.',
	},
	403: {
		title: 'Døra er låst',
		text: 'Du har ikkje tilgang til denne sida.',
	},
	404: {
		title: 'Her var det ingen heime',
		text: 'Sida finst ikkje. Kanskje lenkja er gamal, eller konserten har flytta.',
	},
}

/** plain "Not found"-style texts from `throw new Response(...)` say nothing new */
const isGeneric = (data: unknown) =>
	typeof data !== 'string' ||
	!data.trim() ||
	/^(not found|forbidden|unauthorized|bad request)$/i.test(data.trim())

export function ErrorMessage({
	code,
	title,
	text,
	children,
}: {
	code?: number | string
	title: string
	text?: React.ReactNode
	children?: React.ReactNode
}) {
	return (
		<>
			{code ? <p className="er-code">{code}</p> : null}
			<h1 className="er-title">{title}</h1>
			{text ? <p className="er-text">{text}</p> : null}
			{children}
			<p className="er-actions">
				<Link to="/#program" className="er-btn er-btn-primary">
					Til programmet
				</Link>
				<Link to="/" className="er-btn">
					Framsida
				</Link>
			</p>
		</>
	)
}

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => {
		const copy = STATUS_COPY[error.status] ?? {
			title: 'Noko gjekk gale',
			text: 'Prøv igjen om litt.',
		}
		return (
			<ErrorMessage
				code={error.status}
				title={copy.title}
				text={isGeneric(error.data) ? copy.text : String(error.data)}
			/>
		)
	},
	statusHandlers,
	unexpectedErrorHandler = (error) => {
		if (typeof navigator !== 'undefined' && !navigator.onLine) {
			return (
				<ErrorMessage
					title="Du er fråkopla"
					text="Kople til nettet og last inn sida på nytt."
				/>
			)
		}
		return (
			<ErrorMessage
				title="Noko gjekk gale"
				text="Vi har fått beskjed og ser på det. Prøv å laste inn sida på nytt."
			>
				<details className="er-details">
					<summary>Teknisk info</summary>
					<pre>{getErrorMessage(error)}</pre>
				</details>
			</ErrorMessage>
		)
	},
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => React.JSX.Element | null
}) {
	const error = useRouteError()
	captureException(error)
	const params = useParams()

	if (typeof document !== 'undefined') {
		console.error(error)
		reportErrorToServer(error)
	}

	const isResponse = isRouteErrorResponse(error)
	const mood: Mood = isResponse && error.status < 500 ? 'away' : 'flicker'

	return (
		<main className="er">
			<ErrorHouse mood={mood} />
			<div className="er-body">
				{isResponse
					? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
							error,
							params,
						})
					: unexpectedErrorHandler(error)}
			</div>
		</main>
	)
}

/**
 * The festival house at night: dark window and a moon when nobody's home
 * (4xx), a window light that won't settle when something broke (5xx).
 */
function ErrorHouse({ mood }: { mood: Mood }) {
	return (
		<svg
			className="er-house"
			data-mood={mood}
			viewBox="0 0 120 100"
			aria-hidden="true"
		>
			<circle className="er-star" cx="14" cy="18" r="1.2" />
			<circle className="er-star" cx="104" cy="30" r="1" />
			<circle className="er-star" cx="30" cy="6" r="0.9" />
			<path
				className="er-moon"
				d="M96 4a9 9 0 1 0 9 13 7 7 0 1 1-9-13Z"
				fill="#f6d77a"
			/>
			<path
				d="M71 30V17h8v21Z"
				fill="#d2481a"
				stroke="#1c1a22"
				strokeWidth="2"
				strokeLinejoin="round"
			/>
			<path
				d="M24 44 60 12l36 32v48H24Z"
				fill="#323b53"
				stroke="#1c1a22"
				strokeWidth="2.4"
				strokeLinejoin="round"
			/>
			<rect className="er-window" x="51" y="30" width="18" height="15" rx="2" />
			<path
				d="M60 30v15M51 37.5h18"
				stroke="#1c1a22"
				strokeWidth="1.6"
				opacity="0.55"
			/>
			<path
				d="M48 92V62h24v30Z"
				fill="#d2481a"
				stroke="#1c1a22"
				strokeWidth="2"
				strokeLinejoin="round"
			/>
			<circle cx="67" cy="78" r="1.6" fill="#1c1a22" />
			<path d="M4 92h112" stroke="#1c1a22" strokeWidth="2.4" />
		</svg>
	)
}
